import { useEffect, useRef } from "react";

import { ChatMessage, Message } from "./ChatMessage";

interface ChatMessagesProps {
  messages: Message[];
  isLoading?: boolean;
  hasReceivedTokens?: boolean;
}

export const ChatMessages = ({ messages, isLoading, hasReceivedTokens = false }: ChatMessagesProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Deduplicate messages by ID
  const uniqueMessages = messages
    .reduce((acc, message) => {
      // Only add if we haven't seen this ID before
      if (!acc.find((m) => m.id === message.id)) {
        acc.push(message);
      }
      return acc;
    }, [] as Message[]);

  return (
    <div className="relative h-full w-full">
      {/* Top gradient */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-sds-background-light to-transparent pointer-events-none z-10" />

      {/* Scrollable content */}
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-auto px-4 md:px-6 py-4"
      >
        <div className="flex flex-col gap-4">
          {uniqueMessages.map((message, index) => {
            // Mark the last message as streaming if we're loading
            const isLastMessage = index === uniqueMessages.length - 1;
            const isStreamingMessage = isLoading && isLastMessage;

            return (
              <ChatMessage
                key={message.id}
                message={message}
                isStreaming={isStreamingMessage}
                hasReceivedTokens={hasReceivedTokens}
              />
            );
          })}
          {/* Invisible element at the bottom to scroll to */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-sds-background-light to-transparent pointer-events-none z-10" />
    </div>
  );
};
